<template>
  <!-- AI F §1.2.2 — drag and drop or pick files; several files in one go, uploaded one after another -->
  <div class="p-8">
    <woot-modal-header
      :header-title="$t('KNOWLEDGE_BASE.UPLOAD.TITLE')"
      :header-content="$t('KNOWLEDGE_BASE.UPLOAD.DESC')"
    />

    <div
      class="mt-4 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer"
      :class="isOver ? 'border-woot-500 bg-woot-25' : 'border-slate-200'"
      @click="$refs.picker.click()"
      @dragover.prevent="isOver = true"
      @dragleave.prevent="isOver = false"
      @drop.prevent="onDrop"
    >
      <p class="text-sm">{{ $t('KNOWLEDGE_BASE.UPLOAD.DROP_HERE') }}</p>
      <p class="text-xs text-slate-500 mt-1">
        {{ $t('KNOWLEDGE_BASE.UPLOAD.FORMATS') }}
      </p>
      <input
        ref="picker"
        type="file"
        multiple
        class="hidden"
        :accept="accept"
        @change="onPick"
      />
    </div>

    <ul v-if="items.length" class="mt-4 text-sm">
      <li
        v-for="item in items"
        :key="item.id"
        class="flex gap-2 py-2 border-b border-slate-50"
      >
        <span class="flex-1 truncate">{{ item.file.name }}</span>
        <span class="text-slate-500">{{ formatSize(item.file.size) }}</span>
        <span :class="item.error ? 'text-red-700' : 'text-slate-500'">
          {{ item.error || $t(`KNOWLEDGE_BASE.UPLOAD.STATE.${item.state}`) }}
        </span>
      </li>
    </ul>

    <div class="flex justify-end gap-2 mt-4">
      <woot-button variant="clear" @click="$emit('close')">
        {{ $t('KNOWLEDGE_BASE.UPLOAD.CLOSE') }}
      </woot-button>
      <woot-button
        :is-disabled="!pending.length || isUploading"
        :is-loading="isUploading"
        @click="uploadAll"
      >
        {{ $t('KNOWLEDGE_BASE.UPLOAD.START') }}
      </woot-button>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { formatSize } from './helpers';

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = '.pdf,.csv,.xls,.xlsx,.doc,.docx';

export default {
  mixins: [alertMixin],
  data() {
    return { items: [], isOver: false, isUploading: false, accept: ACCEPT };
  },
  computed: {
    pending() {
      return this.items.filter(item => item.state === 'QUEUED');
    },
  },
  methods: {
    formatSize,
    onDrop(event) {
      this.isOver = false;
      this.addFiles(event.dataTransfer.files);
    },
    onPick(event) {
      this.addFiles(event.target.files);
      event.target.value = '';
    },
    addFiles(fileList) {
      Array.from(fileList).forEach(file => {
        // AI F §1.2.4 — the size rule is checked here too, so the user is not made to wait for the upload
        const error =
          file.size > MAX_BYTES ? this.$t('KNOWLEDGE_BASE.UPLOAD.TOO_BIG') : '';
        this.items.push({
          id: `${file.name}-${file.size}-${this.items.length}`,
          file,
          state: error ? 'FAILED' : 'QUEUED',
          error,
        });
      });
    },
    async uploadAll() {
      this.isUploading = true;
      for (const item of this.pending) {
        item.state = 'UPLOADING';
        try {
          await this.$store.dispatch('kbSources/uploadFile', item.file);
          item.state = 'DONE';
        } catch (error) {
          item.state = 'FAILED';
          item.error =
            error?.response?.data?.error ||
            this.$t('KNOWLEDGE_BASE.UPLOAD.FAILED');
        }
      }
      this.isUploading = false;
      // AI F §1.2.2 — the queue is empty now: report the outcome and close, even if some files were refused
      this.reportOutcome();
      this.$emit('close');
    },
    reportOutcome() {
      const uploaded = this.items.filter(item => item.state === 'DONE').length;
      const rejected = this.items.filter(
        item => item.state === 'FAILED'
      ).length;
      this.showAlert(
        rejected
          ? this.$t('KNOWLEDGE_BASE.UPLOAD.SUMMARY_REJECTED', {
              uploaded,
              rejected,
            })
          : this.$t('KNOWLEDGE_BASE.UPLOAD.SUMMARY', { uploaded })
      );
    },
  },
};
</script>
